import { Grid, styled, useTheme } from '@mui/material';
import React from 'react';
import { AnalyticsTrackingEvent } from '../../analytics/events';
import { FacebookIcon, InstagramIcon, PinterestIcon, TiktokIcon, TwitterIcon, YoutubeIcon } from '../../assets/svg';
import { brandclub_colors } from '../../colors';
import {
  DESKTOP_HORIZONTAL_PADDING,
  DESKTOP_SCREEN_SIZE,
  MOBILE_HORIZONTAL_PADDING,
  MOBILE_SCREEN_SIZE,
  TABLET_HORIZONTAL_PADDING,
  TABLET_SCREEN_SIZE,
  UNIVERSAL_STORE_SITE,
} from '../../utils/constants';
import { sendTextCallback } from '../../utils/textMessage';
import BrandclubPhoneInput, { BCPhoneInputVariant } from '../components/BrandclubPhoneInput';
import { GenericContainer } from '../components/GenericContainer';
import { BaseComponentPropsType, SiteBrandingType } from '../type';

const isDefined = <T,>(value: T | undefined | null): value is T => value != null;

const FooterContainer = styled(GenericContainer)({
  zIndex: 1,
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: `0 5%`,
  boxSizing: 'border-box',
  backgroundColor: brandclub_colors.blueWhite,
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    padding: `0 ${TABLET_HORIZONTAL_PADDING}px`,
  },
  [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
    padding: `0 ${MOBILE_HORIZONTAL_PADDING}px`,
  },
});

const GetTheAppTitle = styled('div')(({ theme }) => ({
  fontSize: 16,
  fontWeight: 600,
  paddingBottom: 12,
  color: theme.palette.primary.main,
  maxWidth: 301,
}));

const FooterContentContainer = styled(Grid)(({ theme }) => ({
  '&.last-footer-content': {
    borderTop: '1px solid #e5e5e5',
  },
  padding: '54px 0',
  maxWidth: DESKTOP_SCREEN_SIZE - DESKTOP_HORIZONTAL_PADDING * 2,
  width: '100%',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  '.invite_sec': {
    flex: 4,
    minWidth: 300,
    '.brandName': {
      fontSize: 20,
      fontWeight: 700,
      paddingBottom: 12,
      color: theme.palette.primary.main,
      maxWidth: 301,
    },

    '.subtitle': {
      fontSize: 14,
      fontWeight: 500,
      paddingBottom: 30,
      color: theme.palette.primary.main,
      maxWidth: 286,
    },
    '.phone_input': {
      maxWidth: 285,
    },
  },
  '.links': {
    flex: 6,
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      flexDirection: 'column',
      gap: 40,
    },
  },
  '.links_sec': {
    minWidth: 160,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    '.label': {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
    },
    '.header': {
      fontSize: 16,
      fontWeight: 'bold',
      fontStretch: 'normal',
      fontStyle: 'normal',
      letterSpacing: 'normal',
      textAlign: 'left',
      color: theme.palette.primary.main,
    },
    a: {
      textDecoration: 'none',
      fontSize: 14,
      maxWidth: 200,
      fontWeight: 500,
      fontStretch: 'normal',
      fontStyle: 'normal',
      marginTop: 15,
      letterSpacing: 'normal',
      textAlign: 'left',
      color: theme.palette.primary.main,
      [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
        marginTop: 7,
        marginBottom: 3,
      },
      [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
        marginTop: 7,
        marginBottom: 3,
      },
    },
    '.desc': {
      fontSize: 16,
      fontWeight: 500,
      fontStretch: 'normal',
      fontStyle: 'normal',
      marginTop: 14,
      marginBottom: 6,
      letterSpacing: 'normal',
      textAlign: 'left',
      color: theme.palette.primary.main,
    },
    input: {
      marginTop: 18,
      marginBottom: 14,
      width: 260,
      height: 42,
      borderRadius: 10,
      color: theme.palette.primary.main,
      outline: 'none',
      paddingLeft: '20px',
      fontSize: 14,
      border: `solid 1px ${theme.palette.primary.main}`,
      backgroundColor: '#ffffff',
    },
    '::placeholder': {
      color: theme.palette.primary.main,
      opacity: 1 /* Firefox */,
    },
    button: {
      marginTop: 6,
      height: 41,
      width: 144,
      color: 'white',
      border: 'none',
      outline: 'none',
      fontSize: 18,
      fontWeight: 500,
      cursor: 'pointer',
      borderRadius: 50,
      position: 'relative',
      backgroundColor: '#16949b',
    },
  },
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    flexDirection: 'column',
    padding: '40px 0',
    gap: 40,
    '.links_sec': {},
  },
}));

const BottomBarContainer = styled(Grid)(({ theme }) => ({
  borderTop: '1px solid #e5e5e5',
  maxWidth: DESKTOP_SCREEN_SIZE - DESKTOP_HORIZONTAL_PADDING * 2,
  width: '100%',
  display: 'flex',
  alignItems: 'center',

  justifyContent: 'space-between',
  flexDirection: 'row',
  '.bot_nav': {
    display: 'flex',
    flxDirecton: 'row',
    gap: 50,

    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
      flexDirection: 'column',
      gap: 15,
    },
    [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      flexDirection: 'column',
      gap: 15,
    },

    a: {
      fontSize: 12,
      fontWeight: 500,
      color: theme.palette.primary.main,
      textDecoration: 'none',
    },
  },
  '.medias': {
    display: 'flex',
    whiteSpace: 'nowrap',
    a: {
      margin: '0 0 0 20px',
      width: 24,
      height: 24,
    },
    img: {
      width: 24,
      height: 24,
    },
  },

  padding: '30px 0 50px 0',
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    alignItems: 'flex-start',
    flexDirection: 'column-reverse',
    '.medias': {
      marginBottom: 30,
      a: {
        margin: '0 10px 0 0',
      },
    },
  },
}));

const BrandLogo = styled('div')({
  marginBottom: 20,
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    position: 'relative',
    top: 'unset',
    left: 'unset',
    maxWidth: 75,
  },
  img: {
    objectFit: 'contain',
    maxWidth: 125,
    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
      maxWidth: 75,
    },
  },
});

const LinkOffsetPill = styled('div')(({ theme }) => ({
  fontSize: 12,
  fontWeight: 600,
  paddingBottom: 12,
  backgroundColor: theme.palette.primary.main,
  color: '#ffffff',
  height: 24,
  padding: '0 13px',
  display: 'flex',
  alignItems: 'center',
  borderRadius: 200,
}));
interface BrandclubSingleFooterProps extends Partial<BaseComponentPropsType> {
  LinkComponent: BaseComponentPropsType['LinkComponent'];
  axios: BaseComponentPropsType['axios'];
  brandEntity: BaseComponentPropsType['brandEntity'];
  footerConfig: BaseComponentPropsType['footerConfig'];
  className?: string;
  PhoneInputComponent?: React.ComponentType<any>;
}

export interface BrandclubFooterProps extends Omit<BrandclubSingleFooterProps, 'footerConfig'> {
  footerConfigs: BaseComponentPropsType['footerConfig'][] | undefined;
  socialLinks?: {
    facebook?: string;
    instagram?: string;
    x?: string;
    pinterest?: string;
    youtube?: string;
    tiktok?: string;
  };
  PhoneInputComponent?: React.ComponentType<any>;
}

const SingleFooter = ({
  axios,
  LinkComponent,
  brandEntity,
  footerConfig,
  className,
  userProfile,
  trackingParams,
}: BrandclubSingleFooterProps) => {
  return (
    <FooterContentContainer className={className}>
      {footerConfig?.siteBrandingType === SiteBrandingType.UniversalStore ? (
        <div className="invite_sec">
          <div className="logo">
            <BrandLogo>
              <LinkComponent to={'/'}>
                <img
                  loading="lazy"
                  src={'https://media.brandclub.com/brandclub/image_asset/brandclub_logo_no_star.svg'}
                  alt="bd_logo"
                ></img>
              </LinkComponent>
            </BrandLogo>
          </div>

          <GetTheAppTitle
            className="title"
            sx={{
              paddingBottom: userProfile ? '30px' : undefined,
            }}
          >
            Download the app to access rewards
          </GetTheAppTitle>
          {!userProfile && <div className="subtitle">Enter your mobile number to get started.</div>}
          <div className="phone_input">
            <BrandclubPhoneInput
              userProfile={userProfile}
              hasHeader={false}
              buttonText="Send"
              variant={BCPhoneInputVariant.DRAWER_MOBILE}
              handleSendText={async (phoneNumber: string) => {
                return sendTextCallback(
                  phoneNumber,
                  axios,
                  brandEntity,
                  undefined,
                  trackingParams as BaseComponentPropsType['trackingParams'],
                  AnalyticsTrackingEvent.WEB_APP_SOCIAL_LINK_TEXT_APP_DOWNLOAD_LINK,
                );
              }}
            />
          </div>
        </div>
      ) : footerConfig?.siteBrandingType === SiteBrandingType.CustomDTCStore ? (
        <div className="invite_sec">
          <div className="brandName">{brandEntity?.entity?.name}</div>
          <div className="subtitle">{footerConfig.customText}</div>
        </div>
      ) : (
        <div className="invite_sec">
          <div className="brandName">{brandEntity?.entity?.name}</div>
          <div className="subtitle">{`Earn rewards with ${brandEntity?.entity?.name} by completing activities, making purchases,
and syncing your accounts.`}</div>
        </div>
      )}
      <div className="links">
        {footerConfig?.linkSections?.map((section, idx) => {
          return (
            <div className="links_sec" key={section.title ?? idx}>
              {section.title && <div className="header">{section.title}</div>}
              {section?.links?.map((link) => (
                <LinkComponent key={`${link.label}-${section.title}`} to={link.link} onClick={link.onClick}>
                  <div className="label">
                    <div className="label_text">{link.label}</div>
                    {link.offsetText ? <LinkOffsetPill>{link.offsetText}</LinkOffsetPill> : null}
                  </div>
                </LinkComponent>
              ))}
            </div>
          );
        })}
      </div>
    </FooterContentContainer>
  );
};
export const BrandclubFooter = ({
  axios,
  LinkComponent,
  brandEntity,
  footerConfigs,
  className,
  userProfile,
  socialLinks,
  PhoneInputComponent,
}: BrandclubFooterProps) => {
  const theme = useTheme();

  const linkPrefix =
    footerConfigs?.[0]?.siteBrandingType === SiteBrandingType.CustomDTCStore ? UNIVERSAL_STORE_SITE : '';

  return (
    <FooterContainer className={className}>
      {footerConfigs?.filter(isDefined).map((footerConfig, index, allConfigs) => {
        return (
          <SingleFooter
            key={footerConfig.siteBrandingType}
            axios={axios}
            LinkComponent={LinkComponent}
            brandEntity={brandEntity}
            footerConfig={footerConfig}
            userProfile={userProfile}
            PhoneInputComponent={PhoneInputComponent}
            className={allConfigs.length > 1 && allConfigs.length - 1 === index ? 'last-footer-content' : ''}
          />
        );
      })}
      <BottomBarContainer>
        <div className="bot_nav">
          {footerConfigs?.[0]?.siteBrandingType === SiteBrandingType.CustomDTCStore ||
          footerConfigs?.[0]?.siteBrandingType === SiteBrandingType.CustomStore ? null : (
            <LinkComponent to={`/`}>© 2025 Brandclub</LinkComponent>
          )}
          <LinkComponent to={`${linkPrefix}/privacy`}>Privacy Policy</LinkComponent>
          <LinkComponent to={`${linkPrefix}/californiaprivacy`}>California Policy</LinkComponent>
          <LinkComponent to={`${linkPrefix}/terms`}>Terms of Service</LinkComponent>
          <LinkComponent to={`${linkPrefix}/endorsement`}>Endorsement</LinkComponent>
          <LinkComponent to={`${linkPrefix}/rewardterms`}>Reward Terms</LinkComponent>
        </div>
        <div className="medias">
          {
            <>
              {socialLinks?.youtube && (
                <a href={socialLinks.youtube} target="blank" className="social-links" title="YouTube">
                  <YoutubeIcon fill={theme.dtc ? theme.palette.primary.main : undefined} />
                </a>
              )}
              {socialLinks?.instagram && (
                <a href={socialLinks?.instagram} target="blank" title="Instagram">
                  <InstagramIcon fill={theme.dtc ? theme.palette.primary.main : undefined} />
                </a>
              )}
              {socialLinks?.facebook && (
                <a href={socialLinks?.facebook} target="blank" title="Facebook">
                  <FacebookIcon fill={theme.dtc ? theme.palette.primary.main : undefined} />
                </a>
              )}
              {socialLinks?.tiktok && (
                <a href={socialLinks?.tiktok} target="blank" title="TikTok">
                  <TiktokIcon fill={theme.dtc ? theme.palette.primary.main : undefined} />
                </a>
              )}
              {socialLinks?.x && (
                <a href={socialLinks?.x} target="blank" title="X">
                  <TwitterIcon fill={theme.dtc ? theme.palette.primary.main : undefined} />
                </a>
              )}
              {socialLinks?.pinterest && (
                <a href={socialLinks?.pinterest} target="blank" title="Pinterest">
                  <PinterestIcon fill={theme.dtc ? theme.palette.primary.main : undefined} />
                </a>
              )}
            </>
          }
        </div>
      </BottomBarContainer>
    </FooterContainer>
  );
};
